/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
// TODO: fix these two eslint exceptions

import { useState } from 'react';
import { css } from '@emotion/react';
import { tokens, Icon } from '@hotjar/hotsauce';
import { Button } from '@hotjar/webapp/components/Button';
import styled from '@emotion/styled';
import Tippy from '@tippyjs/react';
import 'tippy.js/themes/light-border.css';
import type { Instance, Props } from 'tippy.js';

import type { IconName } from '@hotjar/hotsauce';

const StyledTippy = styled(Tippy)`
  background: ${tokens.color.ui_background};
  ${tokens.elevation(2)};
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.m};

  .tippy-content {
    padding: 0;
  }

  .tippy-box {
    background-color: ${tokens.color.ui_background};
  }

  &[data-placement^='right'] > .tippy-arrow::before {
    border-right-color: ${tokens.color.ui_background} !important;
  }

  &[data-placement^='bottom'] > .tippy-arrow::before {
    border-bottom-color: ${tokens.color.ui_background} !important;
  }

  &[data-placement^='top'] > .tippy-arrow::before {
    border-top-color: ${tokens.color.ui_background} !important;
  }

  &[data-placement^='left'] > .tippy-arrow::before {
    border-left-color: ${tokens.color.ui_background} !important;
  }
`;

interface DropdownContentProps {
  children: React.ReactNode;
  hide: () => void;
}

const DropdownContent = ({ children, hide }: DropdownContentProps): JSX.Element => {
  return (
    <div
      role="dialog"
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        box-shadow: 0 2px 25px 6px rgba(0, 0, 0, 0.1);
        border-radius: ${tokens.spacer.x0};
      `}
      onClick={hide}
    >
      {children}
    </div>
  );
};

interface ListItemProps {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  icon?: IconName;
  disabled?: boolean;
}

export const ListItem = ({ onClick, children, icon, disabled }: ListItemProps): JSX.Element => {
  return (
    <Button
      disabled={disabled}
      css={css`
        padding: 0 20px;
        clear: both;
        font-weight: 400;
        white-space: nowrap;
        font-size: 12px;
        height: 36px;
        color: ${tokens.color.text_default};
        width: 100%;
        text-align: initial;
        border-radius: 0;

        &:hover,
        &:active,
        &:focus {
          color: ${tokens.color.text_default};
          text-decoration: none;
          background-color: ${tokens.color.neutral_100};
        }
      `}
      variant="clean"
      onClick={onClick}
    >
      {icon && (
        <span
          css={css`
            margin-right: 10px;
          `}
        >
          <Icon icon={icon} color="inherited" />
        </span>
      )}
      {children}
    </Button>
  );
};

interface LinkListItemProps {
  href: string;
  children: React.ReactNode;
  icon?: IconName;
  disabled?: boolean;
}

// TODO: Migrate to Dropdown from design system once available.
//       LinkListItem so far is a dependency of a single module
export const LinkListItem = (props: LinkListItemProps): JSX.Element => {
  const { href, children, icon, disabled } = props;

  if (disabled) {
    return (
      <ListItem disabled icon={icon}>
        {children}
      </ListItem>
    );
  }

  return (
    <a
      href={href}
      css={css`
        display: flex;
        align-items: center;
        padding: 0 20px;
        clear: both;
        font-weight: 400;
        white-space: nowrap;
        font-size: 12px;
        height: 36px;
        color: ${tokens.color.text_default};
        width: 100%;
        text-align: initial;
        border-radius: 0;

        &:hover,
        &:active,
        &:focus {
          color: ${tokens.color.text_default};
          text-decoration: none;
          background-color: ${tokens.color.neutral_100};
        }
      `}
    >
      {icon && (
        <span
          css={css`
            margin-right: 10px;
          `}
        >
          <Icon icon={icon} color="inherited" />
        </span>
      )}
      <span>{children}</span>
    </a>
  );
};

interface DropdownMenuProps {
  children: React.ReactElement;
  content: React.ReactChild;
  arrow?: Props['arrow'];
  distance?: number;
  placement?: Props['placement'];
  skidding?: number;
  appendTo?: 'parent' | Element;
  onShow?: () => void;
  onHide?: () => void;
  hideOnClick?: boolean;
  zIndex?: number;
}

export const DropdownMenu = ({
  arrow = false,
  children,
  content,
  distance = 8,
  placement = 'bottom',
  skidding = 0,
  appendTo = 'parent',
  hideOnClick = true,
  onShow = () => null,
  onHide = () => null,
  zIndex = 9999,
}: DropdownMenuProps): JSX.Element => {
  const [instance, setInstance] = useState<Instance<Props> | null>(null);

  return (
    <StyledTippy
      content={instance ? <DropdownContent hide={instance.hide}>{content}</DropdownContent> : ''}
      arrow={arrow}
      onCreate={setInstance}
      interactive
      placement={placement}
      animation="fade"
      offset={[skidding, distance]}
      trigger="click"
      appendTo={appendTo}
      delay={0}
      onShow={onShow}
      onHide={onHide}
      hideOnClick={hideOnClick}
      zIndex={zIndex}
    >
      {children}
    </StyledTippy>
  );
};
